import React from 'react';
import { Link } from 'react-router-dom';
import { 
  RocketLaunchIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  PhotoIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  TagIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const HelpPage: React.FC = () => {
  const steps = [
    {
      icon: <DocumentTextIcon className="h-8 w-8" />,
      title: "Create Account",
      description: "First, register on the site or sign in to your account. This step is essential for identity verification and ad security."
    },
    {
      icon: <TagIcon className="h-8 w-8" />,
      title: "Select Category",
      description: "Choose the appropriate category for your ad. This helps buyers find your ad more easily."
    },
    {
      icon: <DocumentTextIcon className="h-8 w-8" />,
      title: "Complete Information",
      description: "Enter an attractive title, complete description, and technical specifications of your product or service accurately."
    },
    {
      icon: <PhotoIcon className="h-8 w-8" />,
      title: "Add Images",
      description: "Upload high-quality and clear images of your product. Good images have a significant impact on attracting buyers."
    },
    {
      icon: <CurrencyDollarIcon className="h-8 w-8" />,
      title: "Set Price",
      description: "Set a reasonable and competitive price for your product or service. You can also declare the price as negotiable."
    },
    {
      icon: <MapPinIcon className="h-8 w-8" />,
      title: "Add Location",
      description: "Enter the exact location of your product or service. This information helps buyers find you more easily."
    }
  ];

  const tips = [
    {
      icon: <CheckCircleIcon className="h-6 w-6 text-green-500" />,
      title: "Success Tips",
      items: [
        "Use clear and high-quality images",
        "Write complete and accurate descriptions",
        "Set competitive prices",
        "Respond quickly to messages",
        "Keep your contact information updated"
      ]
    },
    {
      icon: <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />,
      title: "Important Notes",
      items: [
        "Avoid posting duplicate ads",
        "Don't include personal information in descriptions",
        "Use appropriate categories",
        "Follow site rules and regulations",
        "Report suspicious ads"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50" dir="ltr">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700">
                  Home
                </Link>
              </li>
              <li>
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              </li>
              <li>
                <span className="text-gray-900 font-medium">Help Center</span>
              </li>
            </ol>
          </nav>
        </div>
      </div>  

      {/* How to Post Ads Section */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-full mb-6">
              <DocumentTextIcon className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-4xl font-semibold text-gray-900 mb-6">How to Post an Ad</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Simple steps to post effective and attractive ads that help you get the best results
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="group relative">
                {/* Connection line for desktop */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 right-[-2rem] w-16 h-0.5 bg-gradient-to-r from-red-200 to-red-300 z-0" />
                )}
                
                <div className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group-hover:border-red-200">
                  {/* Step number badge */}
                  <div className="absolute -top-4 left-6">
                    <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-white text-sm font-semibold">{index + 1}</span>
                    </div>
                  </div>
                  
                  {/* Icon */}
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-red-50 to-red-100 rounded-2xl mb-6 group-hover:from-red-100 group-hover:to-red-200 transition-colors duration-300">
                    <div className="text-red-600 group-hover:text-red-700 transition-colors duration-300">
                      {step.icon}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 group-hover:text-red-700 transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-base">
                    {step.description}
                  </p>
                  
                  {/* Decorative element */}
                  <div className="absolute bottom-4 right-6 w-12 h-1 bg-gradient-to-r from-red-200 to-red-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </div>
            ))}
          </div>
          
          {/* Call to action */}
          <div className="text-center mt-16">
            <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-gray-700 font-medium">Ready to get started?</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tips Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {tips.map((tip, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  {tip.icon}
                  <h3 className="text-lg font-semibold text-gray-900 ml-3">{tip.title}</h3>
                </div>
                <ul className="space-y-2">
                  {tip.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary-600 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-semibold text-white mb-4">Ready to post an ad?</h2>
          <p className="text-xl text-primary-100 mb-8">
            Start now and sell your products or services
          </p>
          <Link
            to="/create-ad"
            className="inline-flex items-center bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-colors duration-300 shadow-lg"
          >
            <DocumentTextIcon className="h-6 w-6 mr-3" />
            Post New Ad
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HelpPage; 